/**
 * F 154 C1 — the raw-markdown toggle round-trip.
 *
 * WHY THIS IS SAFE NOW (F 145 Job B made it safe)
 * ----------------------------------------------
 * F 132 stopped Cut 3 because the raw toggle would corrupt data: the document
 * was stored AS markdown, so editing the markdown and toggling back could lose
 * structure irreversibly. F 145 Job B settled the canonical form: **Block[] is
 * canonical, markdown is DERIVED.** So the toggle is now a pure projection in
 * both directions:
 *
 *     Block[]  --serialise-->  markdown   (view + edit)
 *     Block[]  <--parse------  markdown
 *
 * The blocks never leave canonical storage, and a lossy projection is REPORTED
 * rather than applied. That is the whole difference from F 132.
 *
 * This module is pure — no React, no DOM — so the round-trip is testable
 * without a browser. The UI shell lives in `RawMarkdownEditor.tsx`.
 */

import {
  serializeBlocksToMarkdown,
  isLossless,
  type Drop,
} from '../markdown/serialize-blocks';
import {
  parseMarkdownToBlocks,
  type ParseDrop,
} from '../markdown/parse-blocks';

export type { Drop, ParseDrop };

/** What the user needs to know BEFORE committing a lossy round-trip. */
export interface LossyWarning {
  /** True when nothing would be lost. */
  isLossless: boolean;
  /** Every distinct loss, ready to render. */
  drops: Drop[];
  /** One line, safe to put on a button. */
  summary: string;
  /** The full markdown that WOULD be committed. Shown so the user can judge. */
  preview: string;
}

export type ToggleOutcome =
  /**
   * `lossless` is `boolean`, NOT `true`, on this arm: a caller who passes
   * `force: true` gets `ok: true` from a lossy parse, and typing it as `true`
   * would make that unrepresentable. (It was `true` first; `tsc --strict`
   * caught the lie.)
   */
  | { ok: true; blocks: unknown[]; lossless: boolean }
  | { ok: false; reason: 'empty-markdown'; lossless: true }
  | { ok: false; reason: 'lossy'; lossless: false; warning: LossyWarning };

/**
 * Project canonical blocks into the markdown the raw editor shows.
 *
 * The result ALWAYS carries the loss report. A caller that ignores `drops` is
 * choosing to discard it, which is why the warning is returned rather than
 * thrown: throwing would make "look at this before you lose a callout"
 * impossible to express in a UI.
 */
export function openRawEditor(blocks: unknown): LossyWarning {
  const result = serializeBlocksToMarkdown(blocks);
  const lossless = isLossless(result);
  return {
    isLossless: lossless,
    drops: result.drops,
    summary: summariseDrops(result.drops),
    preview: result.markdown,
  };
}

/**
 * Project edited markdown back into canonical blocks.
 *
 * REFUSES a lossy commit by default. This is the C1 requirement "do not
 * silently drop content": a lossy parse returns `ok: false` with the warning
 * attached, and the caller decides. `force: true` is the explicit override a
 * user gives by clicking "discard the lossy parts" — never a default.
 */
export function closeRawEditor(
  markdown: string,
  opts: { force?: boolean } = {},
): ToggleOutcome {
  const trimmed = markdown.trim();

  // An empty document is a legitimate state (a user clearing everything), not
  // an error. BlockNote rejects an empty document, so surface it distinctly.
  if (trimmed.length === 0) {
    return { ok: false, reason: 'empty-markdown', lossless: true };
  }

  const result = parseMarkdownToBlocks(markdown);
  const lossy = result.drops.length > 0;

  if (lossy && !opts.force) {
    return {
      ok: false,
      reason: 'lossy',
      lossless: false,
      warning: {
        isLossless: false,
        drops: result.drops.map(toDrop),
        summary: summariseDrops(result.drops.map(toDrop)),
        preview: trimmed,
      },
    };
  }

  return { ok: true, blocks: result.blocks, lossless: !lossy };
}

/** `Drop` and `ParseDrop` share a shape; normalise for a single UI. */
function toDrop(d: ParseDrop): Drop {
  return {
    blockType: d.blockType,
    reason: d.reason as Drop['reason'],
    message: d.message,
    count: d.count,
  };
}

/** One line for a notice. Empty means "nothing was lost". */
export function summariseDrops(drops: Array<{ count: number; message: string }>): string {
  if (drops.length === 0) return '';
  const total = drops.reduce((n, d) => n + d.count, 0);
  const kinds = drops.length;
  return (
    `${total} block${total === 1 ? '' : 's'} across ${kinds} kind${kinds === 1 ? '' : 's'} ` +
    `will not survive the markdown round-trip.`
  );
}
